import * as Dialog from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useRef, type CSSProperties, type ReactNode } from "react"
import {
  dialogOverlayProps,
  SCENE_3D_PICKER_ATTR,
  SCENE_3D_STAGE_ATTR,
  SCENE_3D_TOOLBAR_MENU_ATTR,
} from "@/lib/dialog-layer"
import { cn } from "@/lib/utils"

export type WorkspaceDialogSize = "sm" | "md" | "lg" | "xl" | "full"

const SIZE_CLASS: Record<WorkspaceDialogSize, string> = {
  sm: "w-[min(96vw,24rem)]",
  md: "w-[min(96vw,560px)]",
  lg: "w-[min(96vw,700px)]",
  xl: "w-[min(96vw,760px)]",
  full: "w-[min(98vw,72rem)]",
}

export type WorkspaceDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  /** Extra classes on Dialog.Description (e.g. hide subtitle on mobile). */
  descriptionClassName?: string
  /**
   * Заголовок и подпись одной строкой в компактной шапке 52px — как в макете библиотеки.
   * По умолчанию подпись стоит под заголовком: так во всех остальных диалогах воркспейса.
   */
  inlineDescription?: boolean
  closeAriaLabel: string
  size?: WorkspaceDialogSize
  maxHeightClassName?: string
  bodyClassName?: string
  children: ReactNode
  footer?: ReactNode
  contentClassName?: string
  contentStyle?: CSSProperties
  /** Класс обёртки заголовка (слева в шапке). */
  titleWrapClassName?: string
  /** Класс самого заголовка (перебивает размер по умолчанию). */
  titleClassName?: string
  /** Block outside click / interact dismiss (Escape + X still work via Dialog.Close). */
  preventDismiss?: boolean
  /**
   * Raise z-index above fullscreen hosts like ShotPanel (z 100/101), по образцу ImageLightboxDialog.
   * Уровень 2 — для диалога поверх уже вложенного (просмотрщик ассета внутри пикера библиотеки).
   * Уровень 3 — поверх canvas save dialog (z 120/121), например wizard имени/слота.
   */
  nested?: boolean | 2 | 3
  /** Куда вернуть фокус после закрытия; вызов preventDefault отменяет авто-возврат Radix. */
  onCloseAutoFocus?: (event: Event) => void
  /** Куда поставить фокус при открытии; preventDefault отменяет авто-фокус Radix на первой кнопке. */
  onOpenAutoFocus?: (event: Event) => void
  /** Return preventDefault from the event to keep the dialog open on Escape. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  /** Спрятать шапку (immersive fullscreen); title остаётся sr-only для a11y. */
  hideHeader?: boolean
  /** Центральная зона шапки (например тулбар эскиза). */
  headerCenter?: ReactNode
  /** Действия справа перед кнопкой закрытия. */
  headerActions?: ReactNode
}

export function WorkspaceDialog(props: WorkspaceDialogProps) {
  // На время анимации закрытия держим содержимое последнего открытого состояния.
  const shownRef = useRef(props)
  if (props.open) shownRef.current = props
  return <WorkspaceDialogView {...shownRef.current} open={props.open} onOpenChange={props.onOpenChange} />
}

function WorkspaceDialogView({
  open,
  onOpenChange,
  title,
  description,
  descriptionClassName,
  inlineDescription = false,
  closeAriaLabel,
  size = "sm",
  maxHeightClassName,
  bodyClassName,
  children,
  footer,
  contentClassName,
  contentStyle,
  titleWrapClassName,
  titleClassName,
  preventDismiss = false,
  nested = false,
  onCloseAutoFocus,
  onOpenAutoFocus,
  onEscapeKeyDown,
  hideHeader = false,
  headerCenter,
  headerActions,
}: WorkspaceDialogProps) {
  const overlayZ = nested === 3 ? "z-[130]" : nested === 2 ? "z-[112]" : nested ? "z-[110]" : "z-[100]"
  const contentZ = nested === 3 ? "z-[131]" : nested === 2 ? "z-[113]" : nested ? "z-[111]" : "z-[101]"
  const compactHeader = Boolean(headerCenter || headerActions)

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          {...dialogOverlayProps}
          className={cn("ui-dialog-overlay fixed inset-0 bg-[var(--ui-overlay-heavy)] backdrop-blur-sm", overlayZ)}
        />
        <Dialog.Content
          data-workspace-dialog=""
          className={cn(
            "ui-dialog-content fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            contentZ,
            "flex flex-col rounded-lg border border-ui-border bg-ui-bg-elevated shadow-2xl shadow-black/50 focus:outline-none",
            SIZE_CLASS[size],
            maxHeightClassName,
            contentClassName,
          )}
          style={contentStyle}
          onPointerDownOutside={(e) => {
            if (preventDismiss) {
              e.preventDefault()
              return
            }
            const node = e.target
            if (
              node instanceof Element &&
              (node.closest('[role="listbox"]') ||
                node.closest(`[${SCENE_3D_STAGE_ATTR}]`) ||
                node.closest(`[${SCENE_3D_PICKER_ATTR}]`) ||
                node.closest(`[${SCENE_3D_TOOLBAR_MENU_ATTR}]`))
            ) {
              e.preventDefault()
            }
          }}
          onInteractOutside={(e) => {
            if (preventDismiss) {
              e.preventDefault()
              return
            }
            const node = e.target
            if (
              node instanceof Element &&
              (node.closest('[role="listbox"]') ||
                node.closest(`[${SCENE_3D_STAGE_ATTR}]`) ||
                node.closest(`[${SCENE_3D_PICKER_ATTR}]`) ||
                node.closest(`[${SCENE_3D_TOOLBAR_MENU_ATTR}]`))
            ) {
              e.preventDefault()
            }
          }}
          onCloseAutoFocus={onCloseAutoFocus}
          onOpenAutoFocus={onOpenAutoFocus}
          onEscapeKeyDown={onEscapeKeyDown}
        >
          {hideHeader ? (
            <>
              <Dialog.Title className="sr-only">{typeof title === "string" ? title : closeAriaLabel}</Dialog.Title>
              <Dialog.Description className="sr-only">
                {typeof description === "string" ? description : closeAriaLabel}
              </Dialog.Description>
            </>
          ) : (
            <div
              className={cn(
                "flex border-b border-ui-border",
                inlineDescription
                  ? "h-[52px] shrink-0 items-center justify-between gap-3 px-4"
                  : compactHeader
                    ? "relative z-10 shrink-0 items-center gap-1.5 px-3 py-2 lg:px-4"
                    : "items-start justify-between gap-3 p-5 pb-4",
              )}
            >
              <div
                className={cn(
                  compactHeader ? "min-w-0 shrink" : "min-w-0",
                  inlineDescription && "flex items-baseline gap-3",
                  compactHeader && !titleWrapClassName && "max-w-[min(12rem,28vw)] sm:max-w-[18rem]",
                  titleWrapClassName,
                )}
              >
                <Dialog.Title
                  className={cn(
                    "font-semibold text-ui-text-primary",
                    compactHeader && "min-w-0",
                    inlineDescription ? "shrink-0 text-[15px]" : compactHeader ? "text-base" : "text-lg",
                    titleClassName,
                  )}
                >
                  {title}
                </Dialog.Title>
                {description ? (
                  <Dialog.Description
                    className={cn(
                      "text-xs text-ui-text-secondary",
                      inlineDescription ? "truncate" : "mt-1",
                      descriptionClassName,
                    )}
                  >
                    {description}
                  </Dialog.Description>
                ) : (
                  <Dialog.Description className="sr-only">
                    {typeof title === "string" ? title : closeAriaLabel}
                  </Dialog.Description>
                )}
              </div>
              {headerCenter ? (
                <div className="flex min-w-0 flex-1 items-center justify-center overflow-visible">{headerCenter}</div>
              ) : null}
              <div className="flex shrink-0 items-center gap-1.5">
                {headerActions}
                <Dialog.Close
                  type="button"
                  className={cn(
                    "shrink-0 rounded-lg text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary",
                    inlineDescription
                      ? "flex h-7 w-7 items-center justify-center"
                      : compactHeader
                        ? "flex h-8 w-8 items-center justify-center p-0"
                        : "p-1.5",
                  )}
                  aria-label={closeAriaLabel}
                >
                  <X className={inlineDescription || compactHeader ? "h-4 w-4" : "h-5 w-5"} />
                </Dialog.Close>
              </div>
            </div>
          )}
          <div className={cn("min-h-0 flex-1", bodyClassName)}>{children}</div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ui-border p-4">{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
